/**
 * IDIOMA · El texto que ve el jugador, en un solo sitio.
 *
 * Hoy el juego está en español y ese texto vive repartido por 38 archivos. Esto
 * no es "preparar la traducción al inglés": es el paso ANTERIOR, y hace falta
 * aunque nunca se traduzca nada. Con las frases dispersas no se puede responder
 * a preguntas que ya nos hemos hecho —¿decimos "sala" o "partida"?, ¿los botones
 * llevan punto final?— sin leerse el juego entero.
 *
 * Ver `docs/IDIOMA.md` para el inventario, el glosario y qué queda por decidir.
 *
 * ## Cómo está pensado
 *
 * **Sin dependencias**, como todo lo demás aquí. Una biblioteca de i18n trae un
 * formateador de mensajes completo (ICU, género, ordinales) y de eso hace falta
 * el 5 %: interpolar un nombre y elegir entre singular y plural.
 *
 * **El catálogo español ES el tipo.** `TextKey` sale de las claves de `es.ts`,
 * así que:
 *
 *   - pedir una clave que no existe no compila;
 *   - un idioma al que le falte una clave no compila.
 *
 * Eso segundo es lo que importa de verdad. La forma normal de que una traducción
 * salga mal no es una frase mal traducida: es una pantalla a medio traducir que
 * nadie revisó porque no falla en ningún sitio.
 *
 * **El servidor no manda texto, manda CÓDIGOS.** Ya se hacía en las cuentas
 * (`mensajeDe(reason)` traduce `bad-ticket` a una frase). Es la regla, y con
 * varios idiomas deja de ser una preferencia: el servidor no sabe en qué idioma
 * está mirando quien recibe la respuesta, y en una sala con gente de dos países
 * no hay un idioma correcto que pudiera elegir.
 */
import { ES } from "./es";

/** Las claves válidas. Sale del catálogo español: él manda. */
export type TextKey = keyof typeof ES;

/** Un catálogo completo. `Record` y no `Partial`: faltar una clave no compila. */
export type Catalog = Record<TextKey, string>;

/** Idiomas que existen. Se añade uno aquí y el tipo obliga a completarlo. */
export type Locale = "es";

const CATALOGOS: Record<Locale, Catalog> = { es: ES };

let actual: Locale = "es";

/** Idioma en uso. */
export const locale = (): Locale => actual;

/** Cambia el idioma. Devuelve `false` si no existe, en vez de dejar el juego mudo. */
export function setLocale(l: string): boolean {
  if (!(l in CATALOGOS)) return false;
  actual = l as Locale;
  return true;
}

/**
 * Sustituye `{nombre}` por lo que se le pase.
 *
 * Un marcador sin valor se deja TAL CUAL, con sus llaves. Es feo a propósito:
 * una cadena vacía en su sitio se lee como una frase normal a la que le falta
 * una palabra, y eso llega a producción; `{nick}` en pantalla no.
 */
function interpolar(plantilla: string, params?: Record<string, string | number>): string {
  if (!params) return plantilla;
  return plantilla.replace(/\{(\w+)\}/g, (todo, clave: string) =>
    clave in params ? String(params[clave]) : todo);
}

/**
 * El texto de una clave, en el idioma en uso.
 *
 * Si el idioma actual no la tuviera (no debería: el tipo lo impide), cae al
 * español en vez de devolver la clave. Ver un identificador en pantalla es peor
 * que verlo en el idioma equivocado.
 */
export function t(key: TextKey, params?: Record<string, string | number>): string {
  const cat = CATALOGOS[actual];
  return interpolar(cat[key] ?? ES[key], params);
}

/**
 * Singular o plural según el número.
 *
 * El español y el inglés solo distinguen dos formas, así que dos formas bastan.
 * Se deja como función aparte —y no metiendo la lógica en `t`— porque el día que
 * haga falta un idioma con más formas (polaco, ruso, árabe) el cambio se ve
 * aquí y no repartido por el catálogo.
 *
 * El número se interpola como `{n}` sin que haya que pasarlo a mano: es el
 * parámetro que SIEMPRE está, y olvidarlo daba frases como "jugadores" sueltas.
 */
export function plural(
  n: number,
  formas: { uno: TextKey; otros: TextKey },
  params?: Record<string, string | number>,
): string {
  return t(n === 1 ? formas.uno : formas.otros, { n, ...params });
}

/**
 * El idioma que pide el navegador, si lo tenemos; si no, español.
 *
 * No se llama sola: quien arranca el juego decide cuándo, porque la preferencia
 * guardada por el jugador manda sobre la del navegador.
 */
export function localeDelNavegador(idiomas: readonly string[] = []): Locale {
  for (const raw of idiomas) {
    const base = raw.toLowerCase().split("-")[0];
    if (base in CATALOGOS) return base as Locale;
  }
  return "es";
}
